import { useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';

import { dropIndex, shiftFor } from './move.js';
import type { Rect } from './move.js';

/**
 * Hold-and-drag reordering, in one hook and no dependency.
 *
 * Pointer Events mean a finger and a mouse are the same code path, and pointer
 * capture means the drag survives the finger leaving the row — which it always
 * does, because thumbs travel in arcs. The handle sets `touch-action: none` so
 * the browser hands us the gesture instead of scrolling the page with it.
 *
 * Rows are measured once, when the drag starts. Nothing reflows mid-drag, so
 * the only thing changing per frame is a transform on each row.
 */

type Geometry = { rects: Rect[]; gap: number; startY: number };
type Drag = { from: number; to: number; dy: number };

export type DragSort = {
  /** Put this on the list element — its children are the rows we measure. */
  listRef: (node: HTMLElement | null) => void;
  /** Index currently being dragged, or null. */
  dragging: number | null;
  handleProps: (index: number) => {
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  };
  rowStyle: (index: number) => CSSProperties | undefined;
};

export function useDragSort(onReorder: (from: number, to: number) => void): DragSort {
  const list = useRef<HTMLElement | null>(null);
  const geometry = useRef<Geometry | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);

  const begin = (index: number, event: PointerEvent<HTMLElement>) => {
    // Right-click and middle-click are not drags.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const node = list.current;
    if (!node) return;

    const rects = [...node.children].map((child) => {
      const box = child.getBoundingClientRect();
      return { top: box.top, height: box.height };
    });
    const first = rects[0];
    const second = rects[1];
    const gap = first && second ? second.top - first.top - first.height : 0;

    geometry.current = { rects, gap, startY: event.clientY };
    // Optional because jsdom has no pointer capture; a test still gets to
    // exercise the rest of the gesture.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag({ from: index, to: index, dy: 0 });
  };

  const track = (event: PointerEvent<HTMLElement>) => {
    const geo = geometry.current;
    if (!geo || !drag) return;
    const dy = event.clientY - geo.startY;
    setDrag({ from: drag.from, to: dropIndex(geo.rects, drag.from, dy), dy });
  };

  const finish = () => {
    if (drag && drag.to !== drag.from) onReorder(drag.from, drag.to);
    geometry.current = null;
    setDrag(null);
  };

  const nudge = (index: number, event: KeyboardEvent<HTMLElement>) => {
    const delta = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
    if (delta === 0) return;
    event.preventDefault(); // Otherwise the page scrolls out from under the row.
    onReorder(index, index + delta);
  };

  // One layout per frame, not one per row.
  const geo = geometry.current;
  const shifts = drag && geo ? shiftFor(geo.rects, geo.gap, drag.from, drag.to) : null;

  return {
    listRef: (node) => {
      list.current = node;
    },
    dragging: drag?.from ?? null,
    handleProps: (index) => ({
      onPointerDown: (event) => begin(index, event),
      onPointerMove: track,
      onPointerUp: finish,
      onPointerCancel: finish,
      onKeyDown: (event) => nudge(index, event),
    }),
    rowStyle: (index) => {
      if (!drag || !shifts) return undefined;
      // Transitions live inline rather than in a class because the row being
      // dragged must follow the finger exactly, while the rows getting out of
      // its way should glide — one property, two answers, same element.
      if (index === drag.from) {
        return {
          transform: `translateY(${drag.dy}px)`,
          transition: 'none',
          zIndex: 10,
          position: 'relative',
        };
      }
      return {
        transform: `translateY(${shifts[index] ?? 0}px)`,
        transition: 'transform var(--aj-duration-fast) var(--aj-ease-standard)',
      };
    },
  };
}
